import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { XeroContactModel } from "../CreateBillingUser/CreateBillingUser";
import { BillingCompanyModel } from "../../IccGeneral/CreateReportUser/CreateReportUser";

interface BillingUserModel {
    id: number;
    username: string;
    disabled: number;
    companyID: number;
    clientCurrency: string;
    deduplicationDays: number;
    xeroContactID: string;
    invoiceReference: string;
}

interface AdminGetBillingUsersResponse {
    data: BillingUsersData;
    error: IError;
}

interface BillingUsersData {
    users: BillingUserModel[];
}

interface AdminGetBillingCompaniesResponse {
    data: BillingCompaniesData;
    error: IError;
}

interface BillingCompaniesData {
    companies: BillingCompanyModel[];
}

interface AdminGetXeroContactsResponse {
    data: XeroContactsData;
    error: IError;
}

interface XeroContactsData {
    contacts: XeroContactModel[];
}

interface EditBillingUserResponse {
    data: EditBillingUserData;
    error: IError;
}
interface EditBillingUserData {
    status: string;
}

const allCurrencies: string[] = ["GBP £", "DKK", "EURO €"];

function EditBillingUser() {
    const { enqueueSnackbar } = useSnackbar();

    const [userId, setUserId] = useState<number>(0);

    const [username, setUsername] = useState<string>("");
    const [disabled, setDisabled] = useState<boolean>(false);
    const [selectedCompany, setSelectedCompany] = useState<number>(0);
    const [selectedCurrency, setSelectedCurrency] = useState<string>("GBP £");
    const [deduplicationDays, setDeduplicationDays] = useState<number>(0);
    const [selectedXeroContact, setSelectedXeroContact] = useState<string>("");
    const [invoiceReference, setInvoiceReference] = useState<string>("");

    const [users, setUsers] = useState<BillingUserModel[]>([]);
    const [companies, setCompanies] = useState<BillingCompanyModel[]>([]);
    const [xeroContacts, setXeroContacts] = useState<XeroContactModel[]>([]);

    useEffect(() => {
        GetBillingUsers();
        GetCompanies();
        GetXeroContacts();
    }, []);

    const GetBillingUsers = async () => {
        try {
            const response: AdminGetBillingUsersResponse = await api.get("api/Admin/GetBillingUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetCompanies = async () => {
        try {
            const response: AdminGetBillingCompaniesResponse = await api.get("api/Admin/GetBillingCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCompanies(response.data.companies)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroContacts = async () => {
        try {
            const response: AdminGetXeroContactsResponse = await api.get("api/Xero/GetContacts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroContacts(response.data.contacts)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetUserId(event: any) {
        var value: number = event.target.value;
        setUserId(value);
        var user = null;

        for (var i = 0; i < users.length; i++) {
            if (users[i].id === value) {
                user = users[i]
                break;
            }
        }

        if (user === null)
            return;

        setUsername(user.username);
        setDisabled(user.disabled === 1);
        setSelectedCompany(user.companyID);
        setSelectedCurrency(user.clientCurrency);
        setDeduplicationDays(user.deduplicationDays);
        setSelectedXeroContact(user.xeroContactID);
        setInvoiceReference(user.invoiceReference);
    }

    const EditClient = async () => {
        if (userId === 0 || username === "" || selectedCompany === 0 || selectedXeroContact === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: BillingUserModel = {
            id: userId,
            username: username,
            disabled: disabled ? 1 : 0,
            companyID: selectedCompany,
            clientCurrency: selectedCurrency,
            deduplicationDays: deduplicationDays,
            xeroContactID: selectedXeroContact,
            invoiceReference: invoiceReference
        }

        try {
            const response: EditBillingUserResponse = await api.post("api/Admin/EditBillingUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setUserId(0);
                setUsername("");
                setDisabled(false);
                setSelectedCompany(0);
                setSelectedCurrency("GBP £");
                setDeduplicationDays(0);
                setSelectedXeroContact("");
                setInvoiceReference("");

                // get new users from db
                GetBillingUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    return (
        <Stack>
            {/* Billing user */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Users: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={userId}
                        onChange={SetUserId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select user"}
                        </MenuItem>
                        {users.map((user, index) =>
                            <MenuItem
                                key={index}
                                value={user.id}>
                                {user.username}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue) => { setUsername(newValue.target.value); }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Disabled */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Disabled: </p>
                <Switch
                    checked={disabled}
                    onChange={(newValue) => { setDisabled(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Company */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Company: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCompany}
                        onChange={(newValue: any) => { setSelectedCompany(newValue.target.value); }}
                        size="small">
                        {companies.map((company, index) => <MenuItem
                            key={index}
                            value={company.id}>
                            {company.branchName}
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Currency */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Currenct: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCurrency}
                        onChange={(newValue: any) => { setSelectedCurrency(newValue.target.value); }}
                        size="small">
                        {allCurrencies.map((currency, index) => <MenuItem
                            key={index}
                            value={currency}>
                            {currency}
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Deduplication */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Deduplication days: </p>
                <TextField
                    value={deduplicationDays}
                    onChange={(newValue: any) => { setDeduplicationDays(newValue.target.value); }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* XeroContact */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>XeroContact: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroContact}
                        onChange={(newValue: any) => { setSelectedXeroContact(newValue.target.value); }}
                        size="small">
                        {xeroContacts.map((contanct, index) => <MenuItem
                            key={index}
                            value={contanct.id}>
                            {contanct.name}
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroContacts(); }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Invoice Reference */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Invoice Reference: </p>
                <TextField
                    value={invoiceReference}
                    onChange={(newValue: any) => { setInvoiceReference(newValue.target.value); }}
                    sx={{ minWidth: "290px" }}
                    size="small" />
            </Stack>

            {/* Create and info buttons*/}
            <Button
                variant="outlined"
                onClick={() => EditClient()}
                sx={{ width: '82px' }}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditBillingUser;
